import { GridValidRowModel } from "@mui/x-data-grid";
import { Dispatch, SetStateAction, useState } from "react";
import { editSweepDestWallet } from "src/api/contractSweepRecipientSetting";
import DialogInOne from "src/components/DialogInOne";
import Prompter from "src/components/Prompter";
import { usePageConfigProviderContext } from "src/features/PageConfigProvider";
import { useAlerting, useTranslation, useYubiPrompt } from "src/hooks";
import { useZusDialogStore } from "src/zustand/store";
import EditRecipientDialogContent from "../components/EditRecipientDialogContent";
import { EditRecordFields } from "../types/EditRecipientFields";
import { ViewGroupGetListRes } from "../types/ViewGroupGetListRes";
import { useQueryClient } from "react-query";
import { appError } from "src/utils/errorHandler";

type Props = {
  isOpen: boolean;
  onClose: () => void;
  selectedRow:
    | (GridValidRowModel & { rawData: ViewGroupGetListRes })
    | undefined;
  editRecordFields: EditRecordFields | undefined;
  setEditRecordFields: Dispatch<SetStateAction<EditRecordFields | undefined>>;
};

const useEditRecordDialog = (props: Props) => {
  const {
    isOpen,
    onClose,
    selectedRow,
    editRecordFields,
    setEditRecordFields,
  } = props;
  const queryClient = useQueryClient();
  const { rawData } = selectedRow ?? {};
  const { translatePrefix } = usePageConfigProviderContext();
  const { getResAfterYubi, prompterConfig } = useYubiPrompt();
  const zusDialog = useZusDialogStore();
  const { t } = useTranslation(translatePrefix);
  const [isDoubleConfirm, setIsDoubleConfirm] = useState(false);

  const { alerting } = useAlerting();

  const onConfirm = async () => {
    zusDialog.openExtra("loadingDialog");
    const res: any = await getResAfterYubi(
      editSweepDestWallet,
      {
        chainType: rawData?.chain_type,
        chainId: rawData?.chain_id,
        assetName: rawData?.asset_name,
        riskType: rawData?.risk_type,
        walletId: rawData?.wallet_id,
        probability: editRecordFields?.probability,
        groupId: rawData?.group_id,
        status: editRecordFields?.status,
      },
      {
        isSkipErrorHandling: true,
      },
    );
    zusDialog.closeExtra();

    if (!res) return;

    const { result = null, error = null, requestId = null } = res || {};

    if (error?.code === -69) {
      return alerting(
        "warning",
        t("the_last_sweep_dest_cannot_be_deleted_disabled"),
      );
    }

    if (!result || error) {
      return appError({
        ...error,
        requestId,
      });
    }

    if (result.double_confirm_required) {
      return setIsDoubleConfirm(true);
    }

    alerting("success", t("edit_record_success"));
    queryClient.invalidateQueries({
      queryKey: ["get_recipient_group_view_group"],
    });
    onClose();
  };

  const isConfirmDisabled =
    selectedRow?.rawData?.status.toString() === editRecordFields?.status &&
    selectedRow?.rawData?.probability.toString() ===
      editRecordFields?.probability;

  const renderEditRecordDialog = () => {
    return (
      <>
        <Prompter {...prompterConfig} />
        <DialogInOne
          title={t("edit_record")}
          self={{
            open: isOpen,
            onClose: onClose,
          }}
          content={
            <EditRecipientDialogContent
              selectedRow={selectedRow}
              editRecordFields={editRecordFields}
              setEditRecordFields={setEditRecordFields}
            />
          }
          isConfirmDisabled={isConfirmDisabled}
          onConfirm={onConfirm}
          onCancel={onClose}
        />
      </>
    );
  };

  const onDoubleConfirmed = async () => {
    zusDialog.openExtra("loadingDialog");
    const res: any = await getResAfterYubi(editSweepDestWallet, {
      chainType: selectedRow?.rawData?.chain_type,
      chainId: selectedRow?.rawData?.chain_id,
      assetName: selectedRow?.rawData?.asset_name,
      riskType: selectedRow?.rawData?.risk_type,
      walletId: selectedRow?.rawData.wallet_id,
      probability: editRecordFields?.probability,
      status: editRecordFields?.status,
      groupId: rawData?.group_id,
      actionConfirmed: true,
    });
    zusDialog.closeExtra();

    if (!res) return;

    alerting("success", t("edit_record_success"));
    queryClient.invalidateQueries({
      queryKey: ["get_recipient_group_view_group"],
    });
    onClose();
  };

  const renderEditDoubleConfirmDialog = () => {
    return (
      <Prompter
        onClose={() => setIsDoubleConfirm(false)}
        open={isDoubleConfirm}
        title={t("edit_double_confirm")}
        onYes={onDoubleConfirmed}
      />
    );
  };

  return { renderEditRecordDialog, renderEditDoubleConfirmDialog };
};

export default useEditRecordDialog;
